import { create } from 'zustand';

import { db } from '@/services/storage/db';
import { deleteHarvestRecord as deleteHarvestRecordService } from '@/services/harvest-service';
import { useAuthStore } from '@/stores/auth-store';
import type { HarvestRecord } from '@/types/hunt';

interface HarvestState {
  records: HarvestRecord[];
  isLoading: boolean;

  loadForHunt: (huntId: string) => Promise<void>;
  createRecord: (input: Omit<HarvestRecord, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => Promise<HarvestRecord>;
  updateRecord: (id: string, updates: Partial<HarvestRecord>) => Promise<HarvestRecord>;
  deleteRecord: (id: string) => Promise<void>;
  getRecordsByHunt: (huntId: string) => HarvestRecord[];
}

export const useHarvestStore = create<HarvestState>((set, get) => ({
  records: [],
  isLoading: false,

  loadForHunt: async (huntId) => {
    set({ isLoading: true });
    try {
      const records = await db.getHarvestRecordsByHunt(huntId);
      set((state) => ({
        records: [
          ...state.records.filter((r) => r.huntId !== huntId),
          ...records,
        ],
        isLoading: false,
      }));
    } catch (err) {
      console.error('[HarvestStore] loadForHunt:', err);
      set({ isLoading: false });
    }
  },

  createRecord: async (input) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId) {
      throw new Error('[harvest-store]: cannot create — user not authenticated');
    }
    // Pass userId + input fields only — createHarvestRecord generates id/createdAt/updatedAt.
    // Pre-generating an id here causes a mismatch: the store would hold a stale id
    // while the DB record has a different UUID, breaking photo attachment (DB2-09).
    const saved = await db.createHarvestRecord({ ...input, userId });
    set((state) => ({ records: [...state.records, saved] }));
    return saved;
  },

  updateRecord: async (id, updates) => {
    const updated = await db.updateHarvestRecord(id, {
      ...updates,
      updatedAt: new Date().toISOString(),
    });
    set((state) => ({
      records: state.records.map((r) => (r.id === id ? updated : r)),
    }));
    return updated;
  },

  deleteRecord: async (id) => {
    await deleteHarvestRecordService(id);
    set((state) => ({ records: state.records.filter((r) => r.id !== id) }));
  },

  getRecordsByHunt: (huntId) => get().records.filter((r) => r.huntId === huntId),
}));
